import { content } from '@content';
import {
  FontCases,
  FontFamilies,
  UniversalContainerState,
  FontStyle,
  TextAligns,
  DivContainerState,
} from '@components';

import {
  // DataAttributesPanel,
  DataVisualizationLibraryPanel,
  // LibraryPanel,
} from './LibrariesPanel';
import { ImageLibraryPanel } from './LibrariesPanel';
import { HTMLElementWithDocument } from './SampleTemplate/SampleTemplate.types';

import { Libraries } from './TemplateView.const';
import {
  ChosenImage,
  HTMLImageElementWithOriginalInnerHTML,
  RedefinedButtonStyles,
  RedefinedWysiwygStyles,
} from './TemplateView.types';
import { DataVisualizationListItemSchema } from '@modules';
import { Dispatch, SetStateAction } from 'react';
import { FONT_FAMILY_OPTIONS } from '@components/Wysiwyg/UniversalContainer/UniversalContainer.const';
import { BorderStyle, ButtonSettingsState, DisplayType } from '@components/Wysiwyg/ButtonSettings/ButtonSettings.props';
import { DISPLAY_OPTIONS } from '@components/Wysiwyg/ButtonSettings/ButtonSettings.const';
// import { TreeView } from '@material-ui/lab';

/**
 TemplateView component utils
 @returns {functions}
 */

export const getLibrariesWrapperTitle = (chosenLibrary: Libraries | null) => {
  switch (chosenLibrary) {
    case Libraries.IMAGES:
      return content.images;
    case Libraries.DATA_VISUALIZATIONS:
      return content.dataVisualization;
    case Libraries.DATA_ATTRIBUTES:
      return content.dataAttributes;
    default:
      return content.library;
  }
};

export const getLibraryPanelComponent = (
  chosenLibrary: Libraries | null,
  isImageLibrary: boolean,
  onChooseItem: (item: ChosenImage | DataVisualizationListItemSchema) => void,
  chosenItem?: ChosenImage | DataVisualizationListItemSchema,
  setChosenItem?: any,
  searchKeyword?: string,
) => {
  switch (chosenLibrary) {
    case Libraries.IMAGES:
      return (
        <ImageLibraryPanel
          isImageLibrary={isImageLibrary}
          chosenImage={chosenItem as ChosenImage}
          onChooseImage={onChooseItem}
          searchKeyword={searchKeyword}
        />
      );
    case Libraries.DATA_VISUALIZATIONS:
      return (
        <DataVisualizationLibraryPanel
          isDVLibrary={isImageLibrary}
          chosenDV={chosenItem as DataVisualizationListItemSchema}
          onDVSelect={onChooseItem}
          setChosenDV={setChosenItem as Dispatch<SetStateAction<DataVisualizationListItemSchema>>}
          searchKeyword={searchKeyword}
        />
      );
    default:
      return (
        <ImageLibraryPanel
          isImageLibrary={isImageLibrary}
          chosenImage={chosenItem as ChosenImage}
          onChooseImage={onChooseItem}
          searchKeyword={searchKeyword}
        />
      );
  }
};

function camelize(str: string) {
  return str
    ?.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word: string, index: number) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    ?.replace(/\s+/g, '');
}

export const transformStylesStringToObject = (cssText: string) => {
  const regex = /([\w-]*)\s*:\s*([^;]*)/g;
  let match = {} as unknown as RegExpExecArray | null;
  const properties: { [key: string]: string } = {};

  while ((match = regex.exec(cssText))) {
    properties[camelize(match[1]).replaceAll('-', '')] = match[2].trim();
  }

  return properties;
};

const getWysiwygFontStyle = (stylesObj: { [key: string]: string }): FontStyle[] => {
  const fontStyles: FontStyle[] = [];

  if (stylesObj.fontStyle === 'italic') {
    fontStyles.push(stylesObj.fontStyle);
  }
  if (stylesObj.fontWeight === 'bold') {
    fontStyles.push(stylesObj.fontWeight);
  }
  if (stylesObj.textDecoration === 'underline') {
    fontStyles.push('underlined');
  }

  return fontStyles;
};

const getInitialFontCase = (textTransform: string) => {
  switch (textTransform) {
    case 'lowercase':
      return 'lower';
    case 'uppercase':
      return 'upper';
    case 'capitalize':
      return 'title';
    case 'sentence':
      return 'sentence';
    default:
      return 'original';
  }
};

const getTextTransformStyle = (fontCase: FontCases | undefined) => {
  switch (fontCase) {
    case 'lower':
      return 'lowercase';
    case 'upper':
      return 'uppercase';
    case 'title':
      return 'capitalize';
    case 'sentence':
      return 'sentence';
    default:
      return 'unset';
  }
};

export const getButtonSettingsInitialValues = (
  chosenModuleElemId: string,
  stylesObj: { [key: string]: string },
): ButtonSettingsState => {
  const moduleModal = document?.getElementById('module-modal') as HTMLElementWithDocument;
  const activeElement = moduleModal?.contentDocument.getElementById(chosenModuleElemId);

  // logic for accessing the link URL based on the tag name
  const tagName = activeElement?.tagName.toLowerCase() ?? '';
  let href = '';
  // if tagname is A
  if (tagName === 'a') {
    href = activeElement?.getAttribute('href') ?? '';
  }
  // if tagname is span or img, look for parent A
  else if (tagName === 'span' || tagName === 'img') {
    const parentA = activeElement?.parentElement;

    if (parentA?.tagName.toLowerCase() === 'a') {
      href = parentA?.getAttribute('href') || '';
    }
  }
  // if tagname is div, h1-h6, p look for first child A
  else if (['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
    const firstChildA = activeElement?.firstElementChild;
    href = firstChildA?.getAttribute('href') || '';
  }
  // if tagname is other, set href to empty string
  else {
    href = '';
  }

  // get the title attribute
  const title = activeElement?.getAttribute('title');

  const camelizedFontFamily = camelize(stylesObj.fontFamily?.split(', ')[0].toLowerCase().replaceAll('-', ' '));

  const backgroundImageSrc = stylesObj.backgroundImage
    ? stylesObj.backgroundImage.replace('url', '').replace(/[{("')}]/g, '')
    : 'none';
  // const backgroundColor = stylesObj.backgroundColor ?? 'unset';

  return {
    // backgroundColor: backgroundColor,
    borderColor: stylesObj.borderColor || 'unset',
    borderRadius: +stylesObj.borderRadius?.replace('px', '') || 0,
    borderStyle: (stylesObj.borderStyle as BorderStyle) || 'none',
    borderWidth: +stylesObj.borderWidth?.replace('px', '') || 0,
    boxSizing: stylesObj.boxSizing || undefined,
    display: (stylesObj.display as DisplayType) || 'block',
    fontFamily: (camelizedFontFamily as FontFamilies) || 'arial',
    fontSize: +stylesObj.fontSize?.replace('px', '') || 14,
    fontColor: stylesObj.color,
    fontStyle: getWysiwygFontStyle(stylesObj),
    height: +stylesObj.height?.replace('px', '') || 20,
    textAlign: (stylesObj.textAlign as TextAligns) || 'inherit',
    width: +stylesObj.width?.replace('px', '') || 100,
    fontCase: getInitialFontCase(stylesObj.textTransform),
    buttonBgColor: stylesObj.backgroundColor,
    link: href || '',
    title: title || '',
    backgroundImageSrc: backgroundImageSrc,
  };
};

export const getUniversalInitialValues = (
  chosenModuleElemId: string,
  stylesObj: { [key: string]: string },
): UniversalContainerState => {
  const moduleModal = document?.getElementById('module-modal') as HTMLElementWithDocument;
  const activeElement = moduleModal?.contentDocument.getElementById(chosenModuleElemId);

  // logic for accessing the link URL based on the tag name
  const tagName = activeElement?.tagName.toLowerCase() ?? '';
  let href = '';
  let title = '';

  // if tagname is A
  if (tagName === 'a') {
    href = activeElement?.getAttribute('href') ?? '';
    title = activeElement?.getAttribute('title') ?? '';
  }
  // if tagname is span or img, look for parent A
  else if (tagName === 'span' || tagName === 'img') {
    const parentA = activeElement?.parentElement;

    if (parentA?.tagName.toLowerCase() === 'a') {
      href = parentA?.getAttribute('href') || '';
      title = parentA?.getAttribute('title') ?? '';
    }
  }
  // if tagname is div, h1-h6, look for first child A
  else if (['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
    const firstChildA = activeElement?.firstElementChild;

    if (firstChildA?.tagName.toLowerCase() === 'a') {
      href = firstChildA?.getAttribute('href') || '';
      title = firstChildA?.getAttribute('title') ?? '';
    }
  }
  // if tagname is other, set href to empty string
  else {
    href = '';
    title = '';
  }

  const camelizedFontFamily = camelize(stylesObj.fontFamily?.split(', ')[0].toLowerCase().replaceAll('-', ' '));

  return {
    fontFamily: (camelizedFontFamily as FontFamilies) || 'arial',
    fontSize: +stylesObj.fontSize?.replace('px', '') || 14,
    fontColor: stylesObj.color,
    fontStyle: getWysiwygFontStyle(stylesObj),
    textAlign: (stylesObj.textAlign as TextAligns) || 'inherit',
    fontCase: getInitialFontCase(stylesObj.textTransform),
    buttonBgColor: stylesObj.backgroundColor,
    link: href || '',
    title: title || '',
  };
};

export const convertFromCamelToHyphenCase = (str: string) => {
  return str.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
};

export const convertFromCamelToSpaceCase = (str: string | undefined) => {
  if (str) {
    return str.replace(/[A-Z]/g, (m) => ' ' + m.toLowerCase());
  }

  return undefined;
};

export function convertButtonStylesToString(stylesObj: RedefinedButtonStyles) {
  let styles = '';

  for (const [p, val] of Object.entries(stylesObj)) {
    if (p === 'fontFamily') {
      // Convert fontFamily to hyphen case for CSS
      styles += `font-family: ${convertFromCamelToSpaceCase(val)};\n`;
      continue;
    } else if (p === 'backgroundImageSrc') {
      // Convert backgroundImageSrc to background-image for CSS
      styles += val === 'none' ? `background-image: none;\n` : `background-image: url(${val});\n`;
      continue;
    }

    if (val) {
      styles += `${convertFromCamelToHyphenCase(p)}: ${val};\n`;
    }
  }

  return `\n${styles}`;
}

export function convertWysiwygStylesToString(stylesObj: RedefinedWysiwygStyles) {
  let styles = '';

  for (const [p, val] of Object.entries(stylesObj)) {
    if (p === 'fontFamily') {
      // Convert fontFamily to hyphen case for CSS
      styles += `font-family: ${convertFromCamelToSpaceCase(val)};\n`;
      continue;
    }
    styles += `${p}: ${val};\n`;
  }

  return `\n${styles}`;
}

export const getRedefinedButtonStyles = (stylesObj: ButtonSettingsState) => {
  // find the type of the matching font family in FONT_FAMILY_OPTIONS
  const fontFamilyType: string =
    FONT_FAMILY_OPTIONS.find((option) => option.value === stylesObj.fontFamily)?.type || 'sans-serif';
  const fontFamily = `${convertFromCamelToSpaceCase(stylesObj.fontFamily)}, ${fontFamilyType}`;

  const display = convertFromCamelToHyphenCase(
    DISPLAY_OPTIONS.find((option) => option.value === stylesObj.display)?.value || 'block',
  );

  // set the line height to equal the height of the button
  let lineHeight = stylesObj.height ? `${stylesObj.height}px` : 'unset';
  // if the border style is not none, box-sizing is border-box, and height is set,
  // calculate the line height
  if (
    stylesObj.borderStyle !== 'none' &&
    stylesObj.boxSizing === 'border-box' &&
    stylesObj.height &&
    stylesObj.height > 0
  ) {
    const borderWidth = stylesObj.borderWidth ? stylesObj.borderWidth : 0;
    const height = stylesObj.height ? stylesObj.height : 1; // minimum height is 1px
    const calculatedLineHeight = height - borderWidth * 2;
    lineHeight = calculatedLineHeight > 0 ? `${calculatedLineHeight}px` : '1px';
  }

  const redefinedStyles: RedefinedButtonStyles = {
    backgroundColor: stylesObj.buttonBgColor || 'unset',
    backgroundImage: 'none',
    borderColor: stylesObj.borderColor || 'unset',
    borderRadius: `${stylesObj.borderRadius}px`,
    borderStyle: stylesObj.borderStyle || 'none',
    borderWidth: `${stylesObj.borderWidth}px`,
    boxSizing: stylesObj.boxSizing || 'unset',
    color: stylesObj.fontColor || 'unset',
    display: display,
    fontFamily: fontFamily, // stylesObj.fontFamily,
    fontSize: `${stylesObj.fontSize}px`,
    fontStyle: 'normal',
    fontWeight: 'normal',
    height: stylesObj.height ? `${stylesObj.height}px` : 'unset',
    lineHeight: lineHeight ? lineHeight : 'unset',
    msoHide: stylesObj.msoHide || '',
    textAlign: stylesObj.textAlign,
    textDecoration: 'unset',
    textTransform: getTextTransformStyle(stylesObj.fontCase),
    width: stylesObj.width ? `${stylesObj.width}px` : 'unset',
  };

  if (stylesObj.fontStyle?.includes('bold')) {
    redefinedStyles.fontWeight = 'bold';
  }
  if (stylesObj.fontStyle?.includes('italic')) {
    redefinedStyles.fontStyle = 'italic';
  }
  if (stylesObj.fontStyle?.includes('underlined')) {
    redefinedStyles.textDecoration = 'underline';
  }

  // set up background image styles
  if (stylesObj.backgroundImageSrc && stylesObj.backgroundImageSrc !== 'none') {
    redefinedStyles.backgroundImage = `url(${stylesObj.backgroundImageSrc})`;
    redefinedStyles.backgroundPosition = 'center';
    redefinedStyles.backgroundRepeat = 'no-repeat';
    redefinedStyles.backgroundSize =
      redefinedStyles.width && redefinedStyles.height
        ? `${redefinedStyles.width} ${redefinedStyles.height}`
        : 'contain';
  }

  return redefinedStyles;
};

export const getRedefinedWysiwygStyles = (stylesObj: UniversalContainerState) => {
  // find the type of the matching font family in FONT_FAMILY_OPTIONS
  const fontFamilyType: string =
    FONT_FAMILY_OPTIONS.find((option) => option.value === stylesObj.fontFamily)?.type || 'sans-serif';
  const fontFamily = `${convertFromCamelToSpaceCase(stylesObj.fontFamily)}, ${fontFamilyType}`;

  const redefinedStyles: RedefinedWysiwygStyles = {
    fontFamily: fontFamily, // stylesObj.fontFamily,
    fontSize: `${stylesObj.fontSize}px`,
    color: stylesObj.fontColor || 'unset',
    textAlign: stylesObj.textAlign,
    textTransform: getTextTransformStyle(stylesObj.fontCase),
    fontStyle: 'normal',
    textDecoration: 'unset',
    fontWeight: 'normal',
    backgroundColor: stylesObj.buttonBgColor || 'unset',
  };

  if (stylesObj.fontStyle?.includes('bold')) {
    redefinedStyles.fontWeight = 'bold';
  }
  if (stylesObj.fontStyle?.includes('italic')) {
    redefinedStyles.fontStyle = 'italic';
  }
  if (stylesObj.fontStyle?.includes('underlined')) {
    redefinedStyles.textDecoration = 'underline';
  }

  return redefinedStyles;
};

export const getSentenceCasedText = (elemInnerHTML: string) => {
  const firstLetter = elemInnerHTML[0].toUpperCase();
  const remainingText = elemInnerHTML.slice(1, elemInnerHTML.length).toLowerCase();

  return firstLetter + remainingText;
};

export const getDivInitialValues = (stylesObj: { [key: string]: string }): DivContainerState => {
  return {
    imageSrc: stylesObj.backgroundImage ? stylesObj.backgroundImage.replace('url', '').replace(/[{()}]/g, '') : 'none',
    backgroundColor: stylesObj.backgroundColor !== 'undefined' ? stylesObj.backgroundColor : 'unset',
  };
};

export const getBackgroundImageStyles = (state: DivContainerState, divOffsetWidth: number, divOffsetHeight: number) => {
  const styles = [];

  // if (
  //   state &&
  //   state.backgroundColor &&
  //   state.backgroundColor !== 'undefined' &&
  //   state.backgroundColor !== 'unset' &&
  //   state.imageSrc === 'none'
  // ) {
  if (state && state.backgroundColor && state.backgroundColor !== 'undefined' && state.backgroundColor !== 'unset') {
    // return `background-color: ${state.backgroundColor}; background-image: none;`;
    // return `background-color: ${state.backgroundColor};`;
    styles.push(`background-color: ${state.backgroundColor};`);
  }

  if (state?.imageSrc) {
    // const backgroundImageStyles = `background-size: ${divOffsetWidth}px ${divOffsetHeight}px; background-repeat: no-repeat; background-position: center; background-color: unset;`;

    if (state.imageSrc === 'url():' || state.imageSrc === 'none' || !state.imageSrc) {
      styles.push(`background-image: none;`);
      // return 'background-image: none;';
    }

    // const backgroundImageStyles = `background-size: ${divOffsetWidth}px ${divOffsetHeight}px; background-repeat: no-repeat; background-position: center;`;
    // if (state.imageSrc.includes('url')) {
    //   return `background-image: ${state.imageSrc}; ${backgroundImageStyles}`;
    // }

    // return `background-image: url(${state.imageSrc}); ${backgroundImageStyles}`;

    if (state.imageSrc.includes('url')) {
      styles.push(`background-image: ${state.imageSrc};}`);
    } else if (state.imageSrc !== 'none') {
      styles.push(`background-image: url(${state.imageSrc});`);
    }

    styles.push(
      `background-size: ${divOffsetWidth}px ${divOffsetHeight}px; background-repeat: no-repeat; background-position: center;`,
    );

    return styles.join(' ');
  }

  return '';
};

export const removePreviousConditionalCommentNode = (elem: HTMLElement | null) => {
  if (!elem || !elem.parentNode) return;

  const prevNode = elem.previousSibling;
  // Check if it's a comment node and contains a conditional comment (e.g., "[if" or "<!--[if")
  if (
    prevNode &&
    prevNode.nodeType === Node.COMMENT_NODE &&
    typeof prevNode.nodeValue === 'string' &&
    prevNode.nodeValue.trim().startsWith('[if')
  ) {
    elem.parentNode.removeChild(prevNode);
  }
};

export const prependConditionalComment = (elem: HTMLElement, commentText: string) => {
  if (!elem || !elem.parentNode) return;

  const commentNode = document.createComment(commentText);
  elem.parentNode.insertBefore(commentNode, elem);
};

export const createButtonConditionalCommentVML = (elem: HTMLElement): string => {
  if (!elem || !elem.parentNode) return '';

  // use computed styles to extract styles from the button element
  const computedStyle = window.getComputedStyle(elem);

  const href = encodeURI(elem.getAttribute('href') || '') || '';
  let height = elem.offsetHeight + 'px' || '40px'; // default height if not set
  let width = elem.offsetWidth + 'px' || '120px'; // default width if not set

  if (computedStyle.boxSizing === 'border-box') {
    // If box-sizing is border-box, adjust height to account for border
    const borderWidth = parseInt(computedStyle.borderWidth) || 0;
    height = elem.offsetHeight - borderWidth * 2 + 'px';
    width = elem.offsetWidth - borderWidth * 2 + 'px';
  }

  const arcsize = parseInt(computedStyle.borderRadius) / parseInt(height) || '0';
  const strokecolor = computedStyle.borderColor || 'black';
  const strokeweight = computedStyle.borderWidth || '0';
  const fillColor = computedStyle.backgroundColor || 'white';

  const backgroundImageUrl = elem.style.backgroundImage.match(/url\((['"]?)(.*?)\1\)/);

  const fillSrc = backgroundImageUrl ? backgroundImageUrl[2] : '';
  const buttonText = elem.textContent?.trim() || 'Button';
  const textColor = computedStyle.color || 'black';
  const fontFamily = computedStyle.fontFamily.replaceAll('"', "'") || 'sans-serif';
  const fontSize = computedStyle.fontSize || '14px';
  const fontStyle = computedStyle.fontStyle || 'normal';
  const fontWeight = computedStyle.fontWeight || 'normal';
  const textDecoration = computedStyle.textDecoration || 'none';

  // Compose the VML button markup
  const vml = `
<v:roundrect
  xmlns:v="urn:schemas-microsoft-com:vml"
  xmlns:w="urn:schemas-microsoft-com:office:word"
  href="${href}"
  style="height: ${height}; v-text-anchor: middle; width: ${width}"
  arcsize="${arcsize}"
  strokecolor="${strokecolor}"
  strokeweight="${strokeweight}"
  fill="true"
>
  <v:fill type="tile" src="${fillSrc}" color="${fillColor}" />
  <w:anchorlock />
  <center
    style="
      color: ${textColor};
      font-family: ${fontFamily};
      font-size: ${fontSize};
      font-style: ${fontStyle};
      font-weight: ${fontWeight};
      line-height: ${height};
      text-decoration: ${textDecoration};
    "
  >${buttonText}</center>
</v:roundrect>
`.trim();

  // Wrap in conditional comment
  return `[if mso]>\n${vml}\n<![endif]`;
};

export const terminateStylesString = (styles: string | undefined | null) => {
  if (!styles) return '';

  // Remove any trailing semicolon and whitespace
  const modifiedStyles = styles.trim();
  if (!modifiedStyles.endsWith(';')) {
    return modifiedStyles + ';';
  }
  return modifiedStyles;
};

// check for whether an image is loaded, if so, call the callback immediately, otherwise wait for the load event
export const onImageReady = (imageElem: HTMLImageElementWithOriginalInnerHTML, callback: () => void) => {
  if (imageElem.complete && imageElem.naturalWidth !== 0) {
    // Already loaded
    requestAnimationFrame(() => callback());
  } else {
    imageElem.addEventListener('load', function handler() {
      imageElem.removeEventListener('load', handler);
      requestAnimationFrame(() => callback());
    });
  }
};
